import api from "./api";
import { ok, getMe, can, getLookupsCached, findRecord, recordDetail, splitItems, isoUtc, nameOf, notImplemented, pMap } from "./core";

// Template codes: ROLE-D<designation> for organization-wide templates (HR/Admin) and
// TEAM-<manager>-D<designation> for a manager's own team template (template_scope TEAM).
// [UPDATED] Equal KPI shares under one KRA of a template (2 KPIs = 50 + 50), so the KPIs total 100% without a typed weight.
function kpiShare(lines, line) {
  const group = lines.filter((l) => String(l.parentKraId) === String(line.parentKraId));
  const n = group.length;
  const base = Math.floor(10000 / n) / 100;
  return group.indexOf(line) === n - 1 ? Math.round((100 - base * (n - 1)) * 100) / 100 : base;
}

const CODE = (designationId, managerId = null) => (managerId ? `TEAM-${managerId}-D${designationId}` : `ROLE-D${designationId}`);
const designationOfCode = (code) => {
  const m = /^(?:ROLE|TEAM-.+)-D(\d+)$/.exec(code || "") || /^ROLE-D(\d+)/.exec(code || "");
  return m ? m[1] : null;
};

// ORG: HR/Admin (template.manage). TEAM: manager (template.team.manage), own team only.
export function templateScopeOf(me) {
  if (can(me, "template.manage", ["SYSTEM_ALL", "PERFORMANCE_ORG"]) || can(me, "master.configure", ["SYSTEM_ALL", "PERFORMANCE_ORG"])) return "ORG";
  if (can(me, "template.team.manage", ["TEAM", "SYSTEM_ALL"])) return "TEAM";
  return null;
}

export async function getTemplateAccess() {
  const me = await getMe();
  const scope = templateScopeOf(me);
  return ok({ scope, meId: me.employee_id, canApproveOrg: can(me, "policy.approve") });
}

async function rawTemplates() {
  const { data } = await api.get("/configuration/performance_template");
  return data;
}

function toTemplate(t, lookups, versions) {
  const designationId = designationOfCode(t.template_code);
  const designation = lookups.designations.find((d) => d.id === designationId);
  const line = (x) => {
    const snap = x.definition_snapshot || {};
    const isKra = x.item_type === "KRA";
    return {
      [isKra ? "kraId" : "kpiId"]: String(isKra ? x.kra_id : x.kpi_id),
      name: snap[isKra ? "kra_name" : "kpi_name"] || `${x.item_type} ${isKra ? x.kra_id : x.kpi_id}`,
      measurementMethod: snap.measurement_method || "",
      unit: snap.measurement_unit || "%",
      frequency: snap.measurement_frequency || "",
      direction: snap.measurement_direction || "HIGHER_IS_BETTER",
      weightage: Number(x.weight),
      target: Number(x.target),
      ...(isKra ? {} : { parentKraId: x.parent_kra_id ? String(x.parent_kra_id) : null }),
    };
  };
  return {
    id: t.template_id,
    code: t.template_code,
    scope: t.template_scope || "ORG",
    ownerId: t.owner_employee_id || null,
    ownerName: t.owner_employee_id ? nameOf(t.owner_employee_id) : "",
    version: t.version_no,
    designationId,
    designationName: designation?.name || t.template_name,
    departmentName: "",
    grade: "",
    active: t.status === "APPROVED",
    status: t.status,
    appliedCount: 0,
    updatedAt: isoUtc(t.approved_at || t.created_at),
    updatedBy: nameOf(t.approved_by_employee_id || t.created_by_employee_id, t.template_scope === "TEAM" ? undefined : "HR / Admin"),
    versions,
    kras: (t.items || []).filter((x) => x.item_type === "KRA").map(line),
    kpis: (t.items || []).filter((x) => x.item_type === "KPI").map(line),
  };
}

export async function getRoleTemplates() {
  const [rows, lookups, me] = await Promise.all([rawTemplates(), getLookupsCached(), getMe()]);
  const byCode = {};
  rows.forEach((t) => { (byCode[t.template_code] = byCode[t.template_code] || []).push(t); });
  return ok(Object.values(byCode).map((list) => {
    const sorted = [...list].sort((a, b) => b.version_no - a.version_no);
    const current = sorted.find((t) => t.status === "APPROVED") || sorted[0];
    const newer = sorted.find((t) => t.status !== "APPROVED" && t.version_no > current.version_no) || null;
    const view = toTemplate(current, lookups, sorted.length);
    return {
      ...view,
      ownedByMe: view.scope === "TEAM" && view.ownerId === me.employee_id,
      pendingDraft: newer ? toTemplate(newer, lookups, sorted.length) : null,
    };
  }).sort((a, b) => a.designationName.localeCompare(b.designationName) || a.scope.localeCompare(b.scope)));
}

export async function getRoleTemplate(templateId) {
  const [rows, lookups] = await Promise.all([rawTemplates(), getLookupsCached()]);
  const t = rows.find((x) => x.template_id === Number(templateId));
  if (!t) throw new Error("Template not found.");
  return ok(toTemplate(t, lookups, 1));
}

async function saveVersion(payload) {
  const [me, rows, lookups] = await Promise.all([getMe(), rawTemplates(), getLookupsCached()]);
  const scope = templateScopeOf(me);
  if (!scope) throw new Error("You don't have permission to maintain templates.");
  const designation = lookups.designations.find((d) => d.id === String(payload.designationId))
    || lookups.designations.find((d) => d.name.toLowerCase() === String(payload.designationName || "").trim().toLowerCase());
  if (!designation) throw new Error("Choose a designation from the list (add new ones under Organisation setup).");
  const code = CODE(designation.id, scope === "TEAM" ? me.employee_id : null);
  const version = Math.max(0, ...rows.filter((t) => t.template_code === code).map((t) => t.version_no)) + 1;
  const items = [
    ...(payload.kras || []).map((l) => ({ item_type: "KRA", master_id: Number(l.kraId), weight: Number(l.weightage), target: Number(l.target) })),
    ...(payload.kpis || []).map((l, _i, all) => ({
      item_type: "KPI",
      master_id: Number(l.kpiId),
      parent_kra_id: Number(l.parentKraId),
      // [UPDATED] No KPI weight is typed: the KPIs of one KRA share 100% equally (the backend needs a 100% total).
      weight: kpiShare(all, l),
      target: Number(l.target),
    })),
  ];
  if (items.some((i) => !i.target || i.target <= 0)) throw new Error("Every line needs a default target greater than 0.");
  const name = scope === "TEAM" ? `${designation.name} (team of ${me.employee_name || me.employee_id})` : designation.name;
  const { data } = await api.post("/configuration", {
    kind: "performance_template",
    data: { template_code: code, template_name: name.slice(0, 200), version_no: version, items, reason: "Role template saved" },
  });
  const templateId = data.template_id;
  let note = "Saved as a draft. Someone with policy approval must approve it before it is used.";
  if (scope === "TEAM") note = "Saved as a draft. Approve it and map it to the designation to use it for your team.";
  // Managers approve their own team templates; HR/Admin need policy.approve.
  const mayApprove = scope === "TEAM" || can(me, "policy.approve");
  if (mayApprove && payload.active !== false) {
    await api.post(`/configuration/performance_template/${templateId}/approve`, { reason: "Role template approved" });
    await api.put(`/designations/${designation.id}/performance-template`, { template_id: templateId, reason: "Role template mapped to designation" });
    note = scope === "TEAM"
      ? `Approved and mapped to ${designation.name} for your team. Your direct reports with this designation get it when they are added to a cycle.`
      : null;
  }
  return ok({ id: templateId, note });
}

export const createRoleTemplate = (payload) => saveVersion(payload);
export const updateRoleTemplate = (templateId, payload) => saveVersion(payload);

export const approveRoleTemplate = async (templateId, designationId) => {
  await api.post(`/configuration/performance_template/${templateId}/approve`, { reason: "Role template approved" });
  if (designationId) await api.put(`/designations/${designationId}/performance-template`, { template_id: templateId, reason: "Role template mapped to designation" });
  return ok({ id: templateId });
};

export async function deleteRoleTemplate() {
  throw notImplemented("Templates cannot be deleted in the backend (they are kept for audit). Save a new version instead, or leave it unmapped.");
}

// Same order as the backend when a cycle is applied: the reviewing manager's own team template for the
// designation first, then the organization-wide template.
export async function suggestForEmployee({ designationId, designationName, managerId } = {}) {
  try {
    const { data } = await getRoleTemplates();
    const fits = (t) => t.active && ((designationId && t.designationId === String(designationId)) || (!designationId && t.designationName === designationName));
    const team = managerId ? data.find((t) => fits(t) && t.scope === "TEAM" && t.ownerId === managerId) : null;
    return ok(team || data.find((t) => fits(t) && t.scope === "ORG") || null);
  } catch {
    return ok(null);
  }
}

export async function applyTemplate(cycleId, employeeId, { templateId, replaceExisting = false }) {
  const { data: template } = await getRoleTemplate(templateId);
  const summary = await findRecord(cycleId, employeeId);
  const record = await recordDetail(summary.performance_record_id);
  const { kras, kpis } = splitItems(record);
  if (replaceExisting) {
    await pMap(kpis, (item) => api.delete(`/items/${item.id}`, { data: { reason: "Replaced by role template" } }), 3);
    await pMap(kras, (item) => api.delete(`/items/${item.id}`, { data: { reason: "Replaced by role template" } }), 3);
  }
  const have = new Set(replaceExisting ? [] : [...kras.map((k) => `KRA-${k.kraId}`), ...kpis.map((k) => `KPI-${k.kpiId}`)]);
  const parentItems = new Map(replaceExisting ? [] : kras.map((kra) => [String(kra.kraId), kra.id]));
  const kraLines = template.kras.map((line) => ({ type: "KRA", id: line.kraId, ...line }));
  const kpiLines = template.kpis.map((line) => ({ type: "KPI", id: line.kpiId, ...line }));
  let added = 0;
  for (const line of kraLines) {
    if (have.has(`KRA-${line.id}`)) continue;
    const { data } = await api.post("/assignments", {
      record_ids: [record.performance_record_id],
      assignment: {
        item_type: "KRA",
        master_id: Number(line.id),
        item_title: line.name,
        item_description: "",
        weight: line.weightage,
        target: line.target,
        measurement_unit: line.unit || "%",
        measurement_direction: line.direction || "HIGHER_IS_BETTER",
        due_date: record.measurement_end,
        reason: "Applied from role template",
      },
      reason: "Applied from role template",
    });
    parentItems.set(String(line.id), String(data.item_ids[0]));
    added += 1;
  }
  for (const line of kpiLines) {
    if (have.has(`KPI-${line.id}`)) continue;
    const parentKraItemId = parentItems.get(String(line.parentKraId));
    if (!parentKraItemId) throw new Error(`The parent KRA for ${line.name} is not assigned to this employee.`);
    await api.post("/assignments", {
      record_ids: [record.performance_record_id],
      assignment: {
        item_type: "KPI",
        master_id: Number(line.id),
        parent_kra_item_id: Number(parentKraItemId),
        item_title: line.name,
        item_description: "",
        weight: line.weightage,
        target: line.target,
        measurement_unit: line.unit || "%",
        measurement_direction: line.direction || "HIGHER_IS_BETTER",
        due_date: record.measurement_end,
        reason: "Applied from role template",
      },
      reason: "Applied from role template",
    });
    added += 1;
  }
  return ok({ added });
}

export async function applyTemplateBulk(cycleId, { employeeIds = [], replaceExisting = false, templateId }) {
  const results = await pMap(employeeIds, (id) => applyTemplate(cycleId, id, { templateId, replaceExisting }).then(() => true).catch(() => false), 2);
  return ok({ applied: results.filter(Boolean).length, failed: results.filter((x) => !x).length });
}
